import React, { useEffect, useMemo, useState } from "react";
import {
    Plus, Pencil, Trash2, CheckCircle2, Circle, AlertTriangle, X, Save, Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const PRIORITY_META = {
    high: { label: "Alta", color: "#B33A3A", bg: "#FBEEEE" },
    medium: { label: "Média", color: "#8A6A15", bg: "#FBF4E5" },
    low: { label: "Baixa", color: "#4A6B58", bg: "#EAF1EC" },
};

const STATUS_META = {
    active: { label: "Ativo", color: "#1E3F33", icon: Circle },
    resolved: { label: "Resolvido", color: "#4A6B58", icon: CheckCircle2 },
    inactive: { label: "Inativo", color: "#8B8B8B", icon: Circle },
};

export default function ProblemList({ patientId, consultationId = null, canEdit = true, compact = false }) {
    const [problems, setProblems] = useState([]);
    const [suggested, setSuggested] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [editing, setEditing] = useState(null); // problem id
    const [filterStatus, setFilterStatus] = useState("active");

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/patients/${patientId}/problems`);
            setProblems(data.problems || []);
            setSuggested(data.suggested || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [patientId]);

    const filtered = useMemo(
        () => problems.filter((p) => (filterStatus === "all" ? true : p.status === filterStatus)),
        [problems, filterStatus]
    );

    const importSuggested = async (s) => {
        try {
            await api.post(`/patients/${patientId}/problems`, {
                title: s.title,
                cid10: s.cid10 || null,
                priority: s.priority || "medium",
                status: "active",
                notes: s.description,
                consultation_id: consultationId || s.consultation_id,
            });
            toast.success("Problema adicionado ao Problem List global.");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const removeProblem = async (p) => {
        if (!window.confirm(`Excluir "${p.title}" do Problem List?`)) return;
        try {
            await api.delete(`/patients/${patientId}/problems/${p.id}`);
            toast.success("Problema excluído.");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleStatus = async (p) => {
        const newStatus = p.status === "active" ? "resolved" : "active";
        try {
            await api.put(`/patients/${patientId}/problems/${p.id}`, { status: newStatus });
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const linkToConsult = async (p) => {
        if (!consultationId) return;
        try {
            await api.post(`/patients/${patientId}/problems/${p.id}/link/${consultationId}`);
            toast.success("Vinculado a esta consulta.");
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-muted-clinical">
                <Loader2 className="animate-spin" size={14} /> Carregando problemas...
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="problem-list">
            <div className="flex items-center justify-between flex-wrap gap-2">
                {!compact && (
                    <div>
                        <h3 className="font-display text-lg text-brand">Problemas do Paciente</h3>
                        <p className="text-xs text-muted-clinical">
                            Problem List global — atravessa consultas (POMR)
                        </p>
                    </div>
                )}
                <div className="flex items-center gap-1">
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="input-clinical py-1 text-xs"
                        data-testid="problem-status-filter"
                    >
                        <option value="active">Ativos</option>
                        <option value="resolved">Resolvidos</option>
                        <option value="inactive">Inativos</option>
                        <option value="all">Todos</option>
                    </select>
                    {canEdit && (
                        <button
                            onClick={() => setShowAdd(true)}
                            className="btn-primary text-xs"
                            data-testid="problem-add-btn"
                        >
                            <Plus size={12} /> Novo problema
                        </button>
                    )}
                </div>
            </div>

            {/* Sugestões vindas do último plano de cuidado */}
            {suggested.length > 0 && problems.length === 0 && (
                <div className="card-earthy bg-[#EDE4FF]/40 border-l-4 border-[#5B3F72]">
                    <div className="flex items-start gap-2 mb-2">
                        <Sparkles size={14} className="text-[#5B3F72] mt-0.5" />
                        <p className="text-xs text-[#5B3F72]">
                            <strong>Migração sugerida:</strong> encontramos {suggested.length} problema(s) no último plano
                            de cuidado. Clique para adicionar ao Problem List global.
                        </p>
                    </div>
                    <div className="space-y-1">
                        {suggested.map((s, i) => (
                            <button
                                key={i}
                                onClick={() => importSuggested(s)}
                                className="w-full text-left px-3 py-1.5 bg-white/60 hover:bg-white rounded text-sm flex items-center justify-between gap-2"
                                data-testid={`problem-suggested-${i}`}
                            >
                                <span>
                                    <span className="font-medium text-brand">{s.title}</span>
                                    {s.cid10 && <span className="text-xs text-muted-clinical ml-2">{s.cid10}</span>}
                                </span>
                                <Plus size={14} className="text-[#5B3F72]" />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {filtered.length === 0 ? (
                <div className="text-sm text-muted-clinical italic px-2">
                    Nenhum problema {filterStatus === "active" ? "ativo" : filterStatus === "resolved" ? "resolvido" : "inativo"} registrado.
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((p) => (
                        <ProblemRow
                            key={p.id}
                            problem={p}
                            canEdit={canEdit}
                            consultationId={consultationId}
                            editing={editing === p.id}
                            onStartEdit={() => setEditing(p.id)}
                            onCancelEdit={() => setEditing(null)}
                            onSave={async (patch) => {
                                try {
                                    await api.put(`/patients/${patientId}/problems/${p.id}`, patch);
                                    toast.success("Problema atualizado.");
                                    setEditing(null);
                                    refresh();
                                } catch (e) { toast.error(formatApiError(e)); }
                            }}
                            onToggleStatus={() => toggleStatus(p)}
                            onDelete={() => removeProblem(p)}
                            onLinkConsult={consultationId && !p.related_consultation_ids?.includes(consultationId) ? () => linkToConsult(p) : null}
                        />
                    ))}
                </div>
            )}

            {showAdd && (
                <ProblemForm
                    onCancel={() => setShowAdd(false)}
                    onSave={async (payload) => {
                        try {
                            await api.post(`/patients/${patientId}/problems`, {
                                ...payload,
                                consultation_id: consultationId,
                            });
                            toast.success("Problema adicionado.");
                            setShowAdd(false);
                            refresh();
                        } catch (e) { toast.error(formatApiError(e)); }
                    }}
                />
            )}
        </div>
    );
}

function ProblemRow({ problem, canEdit, consultationId, editing, onStartEdit, onCancelEdit, onSave, onToggleStatus, onDelete, onLinkConsult }) {
    if (editing) {
        return <ProblemForm initial={problem} onCancel={onCancelEdit} onSave={onSave} />;
    }
    const priority = PRIORITY_META[problem.priority] || PRIORITY_META.medium;
    const status = STATUS_META[problem.status] || STATUS_META.active;
    const StatusIcon = status.icon;
    const linkedHere = consultationId && problem.related_consultation_ids?.includes(consultationId);
    return (
        <div
            className="card-earthy border-l-4"
            style={{ borderLeftColor: priority.color }}
            data-testid={`problem-row-${problem.id}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={onToggleStatus}
                            disabled={!canEdit}
                            className="shrink-0"
                            title={problem.status === "active" ? "Marcar como resolvido" : "Reativar problema"}
                            data-testid={`problem-toggle-${problem.id}`}
                        >
                            <StatusIcon size={16} style={{ color: status.color }} />
                        </button>
                        <h4 className={`font-medium ${problem.status === "resolved" ? "line-through text-muted-clinical" : "text-brand"}`}>
                            {problem.title}
                        </h4>
                        {problem.cid10 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-alt text-brand rounded font-mono">
                                {problem.cid10}
                            </span>
                        )}
                        <span
                            className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                            style={{ background: priority.bg, color: priority.color }}
                        >
                            {priority.label}
                        </span>
                        {linkedHere && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] rounded-full font-semibold">
                                Presente nesta consulta
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-muted-clinical mt-1">
                        {problem.onset_date && <>Início: {problem.onset_date} · </>}
                        {problem.resolved_date && <>Resolvido: {problem.resolved_date} · </>}
                        {problem.related_consultation_ids?.length > 0 && (
                            <>Vinculado a {problem.related_consultation_ids.length} consulta{problem.related_consultation_ids.length > 1 ? "s" : ""}</>
                        )}
                    </p>
                    {problem.notes && (
                        <p className="text-sm text-brand-secondary mt-2 whitespace-pre-wrap">{problem.notes}</p>
                    )}
                </div>
                {canEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                        {onLinkConsult && (
                            <button
                                onClick={onLinkConsult}
                                className="btn-ghost text-xs"
                                title="Vincular a esta consulta"
                                data-testid={`problem-link-${problem.id}`}
                            >
                                Vincular
                            </button>
                        )}
                        <button onClick={onStartEdit} className="btn-ghost text-xs" data-testid={`problem-edit-${problem.id}`}>
                            <Pencil size={12} />
                        </button>
                        <button onClick={onDelete} className="btn-ghost text-xs text-red-700" data-testid={`problem-delete-${problem.id}`}>
                            <Trash2 size={12} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function ProblemForm({ initial, onCancel, onSave }) {
    const [title, setTitle] = useState(initial?.title || "");
    const [cid10, setCid10] = useState(initial?.cid10 || "");
    const [priority, setPriority] = useState(initial?.priority || "medium");
    const [status, setStatus] = useState(initial?.status || "active");
    const [onsetDate, setOnsetDate] = useState(initial?.onset_date || "");
    const [notes, setNotes] = useState(initial?.notes || "");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) { toast.error("Informe o título do problema."); return; }
        setSaving(true);
        try {
            await onSave({
                title: title.trim(),
                cid10: cid10.trim() || null,
                priority,
                status,
                onset_date: onsetDate || null,
                notes: notes.trim() || null,
            });
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="card-earthy bg-alt/40 space-y-2" data-testid="problem-form">
            <div className="grid md:grid-cols-6 gap-2">
                <input
                    className="input-clinical md:col-span-3 text-sm py-1"
                    placeholder="Ex.: HAS com hipertrofia ventricular"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="problem-input-title"
                    required
                />
                <input
                    className="input-clinical md:col-span-1 text-sm py-1 font-mono"
                    placeholder="I10"
                    value={cid10}
                    onChange={(e) => setCid10(e.target.value)}
                    data-testid="problem-input-cid"
                />
                <select
                    className="input-clinical md:col-span-1 text-sm py-1"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    data-testid="problem-input-priority"
                >
                    <option value="high">Prioridade Alta</option>
                    <option value="medium">Prioridade Média</option>
                    <option value="low">Prioridade Baixa</option>
                </select>
                <select
                    className="input-clinical md:col-span-1 text-sm py-1"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    data-testid="problem-input-status"
                >
                    <option value="active">Ativo</option>
                    <option value="resolved">Resolvido</option>
                    <option value="inactive">Inativo</option>
                </select>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
                <input
                    type="date"
                    className="input-clinical text-sm py-1"
                    value={onsetDate}
                    onChange={(e) => setOnsetDate(e.target.value)}
                    data-testid="problem-input-onset"
                />
                <input
                    className="input-clinical text-sm py-1"
                    placeholder="Notas curtas (opcional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="problem-input-notes"
                />
            </div>
            <div className="flex justify-end gap-1">
                <button type="button" onClick={onCancel} className="btn-ghost text-xs" data-testid="problem-form-cancel">
                    <X size={12} /> Cancelar
                </button>
                <button type="submit" disabled={saving} className="btn-primary text-xs" data-testid="problem-form-save">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar
                </button>
            </div>
        </form>
    );
}
